// room.ts — LiveKit connection for the listener.
//
// Responsibilities (docs/PROTOCOL.md §2):
//   * Connect to LiveKit with the listener token + livekitUrl returned by /join.
//   * Subscribe to the single audio track named "mix" (Opus stereo) and play it.
//   * Receive data packets on topic "tc" (timecode, lossy) -> TimecodeTracker.
//   * Receive data packets on topic "ctrl" (comments + listener count, reliable).
//   * Poll RTCRtpReceiver getStats() to refine the playout delay (docs §4).

import {
  DisconnectReason,
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
  type RemoteAudioTrack,
} from "livekit-client";
import { TimecodeTracker, type TimecodePacket } from "./timecode";
import type { Comment, CommentConfig } from "./api";

/** Track name the server publishes the Tier-1 stereo mix under (docs §2). */
// Re-exported from conversation.ts (single source of truth for the §9 P6
// listener-side audio routing decision, which is unit-tested there).
export { MIX_TRACK_NAME } from "./conversation";
import { isConversationAudio, MIX_TRACK_NAME } from "./conversation";
/** Data-packet topics (docs §2). */
export const TOPIC_TC = "tc";
export const TOPIC_CTRL = "ctrl";

/** A `ctrl` comment packet (docs §2.2 / §7.4). */
interface CtrlCommentPacket {
  t: "comment";
  id: string;
  projectSec: number;
  text: string;
  author: string;
  ts: number;
  /** Owner clientId (docs §7.4). */
  clientId?: string;
  /** Owner color, hex from the palette (docs §7.4). */
  color?: string;
  /** True when this comment has been edited (docs §7.4). */
  edited?: boolean;
}
/** A `ctrl` comment_edit packet (docs §7.4). */
interface CtrlCommentEditPacket {
  t: "comment_edit";
  id: string;
  text: string;
}
/** A `ctrl` comment_delete packet (docs §7.4). */
interface CtrlCommentDeletePacket {
  t: "comment_delete";
  id: string;
}
/** A `ctrl` comment_config packet — host moderation toggles (docs §7.4). */
interface CtrlCommentConfigPacket {
  t: "comment_config";
  enabled: boolean;
  privateMode: boolean;
}
/** A `ctrl` listeners packet (docs §2.2). */
interface CtrlListenersPacket {
  t: "listeners";
  count: number;
}
interface CtrlStreamStatePacket {
  t: "stream_state";
  paused: boolean;
}
/** A `ctrl` listener_color packet — host recolored a listener (docs §7.4). */
interface CtrlListenerColorPacket {
  t: "listener_color";
  clientId: string;
  color: string;
}
/** A targeted `ctrl` perm packet — this listener's grants changed (§12/§14).
 *  Pushed instantly on a host roster toggle; the health poll reconciles. */
interface CtrlPermPacket {
  t: "perm";
  canPause: boolean;
  canCall: boolean;
}
interface CtrlCommentResolvedPacket { t: "comment_resolved"; id: string; resolved: boolean; resolutionVersion: number }
type CtrlPacket =
  | CtrlCommentResolvedPacket
  | CtrlCommentPacket
  | CtrlCommentEditPacket
  | CtrlCommentDeletePacket
  | CtrlCommentConfigPacket
  | CtrlListenersPacket
  | CtrlStreamStatePacket
  | CtrlListenerColorPacket
  | CtrlPermPacket;

export interface RoomCallbacks {
  /** A new comment arrived over the `ctrl` data channel (docs §7.4). */
  onComment?: (c: Comment) => void;
  /** A comment's text was edited live (docs §7.4 comment_edit). */
  onCommentResolved?: (id: string, resolved: boolean, version: number) => void;
  onCommentEdit?: (id: string, text: string) => void;
  /** A comment was deleted live (docs §7.4 comment_delete). */
  onCommentDelete?: (id: string) => void;
  /** Comment moderation config changed live (docs §7.4 comment_config). */
  onCommentConfig?: (config: CommentConfig) => void;
  /** Host paused/resumed the STREAM (docs §7.4 stream_state) — distinct from a
   *  transport/playback stop (which comes from the `tc` packet's `playing`). */
  onStreamState?: (paused: boolean) => void;
  /** Host recolored a listener (docs §7.4 listener_color) — recolor that
   *  clientId's comments (and the me-chip when it is our own clientId). */
  onListenerColor?: (clientId: string, color: string) => void;
  /** Targeted grant push (§12/§14): this listener's canPause/canCall changed. */
  onPerm?: (canPause: boolean, canCall: boolean) => void;
  /** §16: the set of conversation (talk) tracks changed — refresh the mixer. */
  onConversations?: () => void;
  /** Live listener count changed (`ctrl` listeners packet). */
  onListeners?: (count: number) => void;
  /** Connection state changed (true = connected & media flowing path open). */
  onConnectionChange?: (connected: boolean) => void;
  /** LiveKit gave up reconnecting (terminal disconnect, not an intentional
   *  teardown) — the room will not recover on its own, so the UI must offer a
   *  rejoin instead of showing "Connecting…" forever. */
  onDisconnected?: () => void;
  /** §18: the host removed this participant (forced clean reset). */
  onRemoved?: () => void;
  /** The `mix` audio track was (un)subscribed; isLive reflects audio presence. */
  onMixState?: (live: boolean) => void;
  /** Non-fatal error to surface in the UI. */
  onError?: (message: string) => void;
}

const decoder = new TextDecoder();

/** §26: corner frequency of the per-voice low cut. 120 Hz on the host's ear
 *  (22.08.) — tidier for speech than the classic 100, and still below the
 *  fundamentals that carry a voice: at 12 dB/oct a male 110 Hz sits barely
 *  under the corner, while thumps, fans and room rumble are well inside the
 *  slope. */
const VOICE_LOW_CUT_HZ = 120;

export class ListenerRoom {
  readonly room: Room;
  readonly timecode: TimecodeTracker;

  private audioEl: HTMLAudioElement | null = null;
  /** §14: container for call video tiles (camera/screen of active
   *  participants); provided by the UI, tracks attach/detach themselves. */
  private videoContainer: HTMLElement | null = null;
  private videoEls = new Map<string, HTMLVideoElement>();
  private mixTrack: RemoteAudioTrack | null = null;
  /** §9 P6: hidden <audio> per conversation mic (call-/host-identities), keyed
   *  by track sid. Strictly additive: never feeds the meter, timecode, or the
   *  lossless crossfade — those hang exclusively off the mix track/element.
   *  The elements stay MUTED (WebKit pump only); sound renders via convCtx. */
  private convEls = new Map<string, HTMLAudioElement>();
  /** WebAudio path for conversation voices: playing remote WebRTC audio
   *  through a media element makes iOS treat the whole page as a phone CALL
   *  (volume-rocker floor, ducked playback). Rendering through WebAudio keeps
   *  the page a normal media player. */
  private convCtx: AudioContext | null = null;
  /** Preferred context for conversation voices: the stream player's, which is
   *  already unlocked and running. Only when there is none do we create our
   *  own (which iOS may leave suspended — the cause of one-way call audio). */
  private sharedCtx: AudioContext | null = null;
  private convNodes = new Map<
    string,
    { src: MediaStreamAudioSourceNode; cut: BiquadFilterNode; gain: GainNode }
  >();
  /** Track refs for rebuilding the conversation graph on a fresh context
   *  (iOS un-stick, see refreshConversationRouting). */
  private convTracks = new Map<string, RemoteAudioTrack>();
  /** trackSid -> participant identity. The mixer is keyed by IDENTITY, not by
   *  track: a talker's mic is released and re-published on every auto-mute
   *  cycle (each republish = a NEW track sid), which used to add one more
   *  mixer row per DAW stop. */
  private convIdentity = new Map<string, string>();
  /** identity -> display label (participant name). */
  private convLabels = new Map<string, string>();
  /** §16 mini-mixer: per-PARTICIPANT user gain (0..+10 dB, default unity).
   *  Keyed by identity so a set level survives the talker's mute/unmute
   *  cycles. Multiplied with the global volume/mute — the STREAM fader stays
   *  capped at 0 dB, voices may be boosted above unity. */
  private convUserGain = new Map<string, number>();
  /** Voice master (duck/fade/mute-all); the Stream fader never touches it. */
  private convMaster = 1;
  /** When the conversation graph stopped rendering (0 = it is fine). */
  private convDeadSince = 0;
  /** §17 metering: one analyser per conversation track (POST-gain, so the bar
   *  shows what is actually heard after the mixer fader). */
  private convAnalysers = new Map<string, AnalyserNode>();
  /** §17: own-mic metering (own capture, so no extra permission/session). */
  private micCtx: AudioContext | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private micSrc: MediaStreamAudioSourceNode | null = null;
  private micWiredTrack: MediaStreamTrack | null = null;
  private levelBuf: Float32Array<ArrayBuffer> = new Float32Array(0);
  // Player state, persisted across track re-subscribes / device switches.
  private volume = 1;
  private muted = false;
  /** When true, lossless is the active output, so the Opus element is held silent. */
  private losslessActive = false;
  /** rAF handle for the Opus-element volume crossfade (lossless toggle). */
  private opusFadeRaf: number | null = null;
  private sinkId: string | null = null;
  private statsTimer: number | null = null;
  private connected = false;
  /** Set before an intentional room.disconnect() so the resulting Disconnected
   *  event is not surfaced as a terminal failure to the UI. */
  private intentional = false;
  /** Desired WebRTC jitter-buffer target (ms); the host-chosen listener buffer. */
  private jitterTargetMs = 0;
  // Connection-health tracking (jitter-buffer concealment deltas across stat polls).
  private prevConcealed: number | null = null;
  private prevEmitted: number | null = null;
  private healthWindows: number[] = [];

  // ---- Level metering (Web Audio tap on the mix, read-only) ----------------
  private audioCtx: AudioContext | null = null;
  private analyserL: AnalyserNode | null = null;
  private analyserR: AnalyserNode | null = null;
  private meterBuf: Float32Array<ArrayBuffer> = new Float32Array(0);

  constructor(
    private readonly cb: RoomCallbacks,
    playoutDelayMs?: number,
    private readonly receiveMusic = true,
  ) {
    this.timecode = new TimecodeTracker(playoutDelayMs);
    this.room = new Room({
      // Listener never publishes; keep adaptiveStream/dynacast off — single
      // audio track, no video. Audio playback may require a user gesture
      // (handled by ensureAudioPlayback()).
      adaptiveStream: false,
      dynacast: false,
      // NOTE on iOS call ducking: releasing the mic on mute
      // (publishDefaults.stopMicTrackOnMute) WAS tried against the pumping
      // stream volume — but every auto-mute edge then triggers an audible
      // audio-session route change exactly when playback starts. Reverted:
      // the lesser evil is iOS's voice-processing duck while in a call.
      // Talkback quality: LiveKit's 48 kbps default is audibly "VoIP-thin" —
      // give voices 96 kbps and keep packets flowing in pauses (no DTX).
      publishDefaults: { audioPreset: { maxBitrate: 96_000 }, dtx: false },
      // NO voiceIsolation constraint: field-tested 2026-08-10 — requesting the
      // OS voice mode drags the whole device into the phone-call audio session
      // (volume floor, degraded playback), the exact regression the release-
      // on-mute work fixed. The anti-feedback tools are the GATE (talkgate.ts,
      // outgoing path only) and the talk duck. Desktop users can still enable
      // Voice Isolation manually in the OS mic-mode menu.
      // Capture stays on browser defaults (EC on). Research verdict (WebKit
      // source, bug #311451): on iOS the OUTPUT degradation while a mic is
      // open is UNCONDITIONAL session policy — echoCancellation:false only
      // switches the capture unit (and triggers the left-channel-only bug
      // #281978) without helping playback at all. The real levers are
      // releasing the capture when not talking (done) and pinning
      // navigator.audioSession.type to "playback" between captures (ui.ts).
    });
    this.wireEvents();
  }

  private wireEvents(): void {
    this.room
      .on(RoomEvent.TrackPublished, (pub, participant) => {
        if (!this.receiveMusic && isConversationAudio(pub.kind, pub.trackName, participant.identity)) pub.setSubscribed(true);
      })
      .on(RoomEvent.TrackSubscribed, (track, pub, participant) =>
        this.handleTrackSubscribed(track, pub, participant),
      )
      .on(RoomEvent.TrackUnsubscribed, (track, pub) =>
        this.handleTrackUnsubscribed(track, pub.trackSid),
      )
      .on(RoomEvent.ParticipantDisconnected, (p) => this.dropConversationsOf(p.identity))
      // The mixer lists PROMOTED participants, so its rows must follow
      // promotion + mute changes, not only track (un)subscriptions.
      .on(RoomEvent.ParticipantConnected, () => this.cb.onConversations?.())
      .on(RoomEvent.ParticipantPermissionsChanged, () => this.cb.onConversations?.())
      .on(RoomEvent.TrackMuted, () => this.cb.onConversations?.())
      .on(RoomEvent.TrackUnmuted, () => this.cb.onConversations?.())
      .on(RoomEvent.DataReceived, (payload, participant, _kind, topic) =>
        this.handleData(payload, topic, participant?.identity),
      )
      .on(RoomEvent.Connected, () => this.setConnected(true))
      .on(RoomEvent.Reconnecting, () => this.setConnected(false))
      .on(RoomEvent.Reconnected, () => this.setConnected(true))
      .on(RoomEvent.Disconnected, (reason) => {
        this.setConnected(false);
        if (this.intentional) return;
        // §18: the host removed us — a forced clean reset. The UI reloads the
        // page (fresh identity, immediate rejoin) instead of self-healing in
        // place.
        if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
          this.cb.onRemoved?.();
          return;
        }
        // LiveKit fires Disconnected only after it has exhausted its own
        // Reconnecting/Reconnected retries (transient blips use those events), so
        // an unsolicited Disconnected is terminal — tell the UI to offer a rejoin.
        this.cb.onDisconnected?.();
      })
      .on(RoomEvent.MediaDevicesError, (e) =>
        this.cb.onError?.(`Audio device error: ${(e as Error).message}`),
      );
  }

  private setConnected(v: boolean): void {
    this.connected = v;
    this.cb.onConnectionChange?.(v);
  }

  /** Connect to LiveKit using the join result (docs §3 join `ok`). */
  async connect(livekitUrl: string, token: string): Promise<void> {
    await this.room.connect(livekitUrl, token, { autoSubscribe: this.receiveMusic });
    if (!this.receiveMusic) {
      for (const participant of this.room.remoteParticipants.values()) {
        for (const pub of participant.trackPublications.values()) {
          if (isConversationAudio(pub.kind, pub.trackName, participant.identity)) pub.setSubscribed(true);
        }
      }
    }
    // Any tracks already present at connect time fire TrackSubscribed.
    this.startStatsLoop();
  }

  /**
   * Browsers block autoplay of audio without a user gesture. Call this from a
   * click handler to start/resume playback. Safe to call multiple times.
   */
  async ensureAudioPlayback(): Promise<void> {
    try {
      await this.room.startAudio();
    } catch {
      /* startAudio is best-effort; the element play() below is the real path. */
    }
    // A user gesture is also the moment to resume the metering AudioContext
    // (created suspended under autoplay policy).
    if (this.convCtx && this.convCtx.state !== "running") {
      void this.convCtx.resume().catch(() => {});
    }
    if (this.audioCtx && this.audioCtx.state === "suspended") {
      try {
        await this.audioCtx.resume();
      } catch {
        /* best-effort */
      }
    }
    if (this.audioEl) {
      try {
        await this.audioEl.play();
      } catch (e) {
        this.cb.onError?.(`Playback blocked: ${(e as Error).message}`);
      }
    }
  }

  // ---- Player controls (volume / mute / output device) --------------------

  /** Set playback volume 0..1. */
  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    // MIX only. The conversation used to be multiplied by this too, which
    // conflated the mixer's Stream fader with the voices — the host page
    // (stream at −∞ by design) then heard NO call at all, and the post-gain
    // meters read zero with it. Voices follow convMaster (duck/fade/mute).
    if (this.audioEl && !this.losslessActive) this.audioEl.volume = this.volume;
  }

  /** Master multiplier for the CALL voices only (talk duck, route-change
   *  fade, master mute) — per-person faders stack on top via gainForSid. */
  setConversationMaster(v: number): void {
    this.convMaster = Math.min(1, Math.max(0, v));
    for (const [sid, n] of this.convNodes)
      n.gain.gain.value = this.muted ? 0 : this.convMaster * this.gainForSid(sid);
    for (const [sid, el] of this.convEls) if (!el.muted) el.volume = Math.min(1, this.convMaster * this.gainForSid(sid));
  }
  getVolume(): number {
    return this.volume;
  }
  setMuted(m: boolean): void {
    this.muted = m;
    if (this.audioEl && !this.losslessActive) this.audioEl.muted = m;
    for (const [sid, n] of this.convNodes)
      n.gain.gain.value = m ? 0 : this.convMaster * this.gainForSid(sid);
  }
  isMuted(): boolean {
    return this.muted;
  }

  /** INSTANT WebRTC suppression (while the WS player is the audible path).
   *  MUST use `muted`, not just volume: iOS/WebKit ignores HTMLMediaElement
   *  .volume entirely (it is pinned to 1), so a volume-based kill switch left
   *  BOTH paths audible on iPhones (the "doppelt audio"/phasing bug). volume 0
   *  is set as well for browsers where it works. */
  /** Park every WebRTC audio element for the length of a rebuild gap: they
   *  otherwise play right through it and keep the iOS audio session alive,
   *  which is what a Bluetooth sink waits on before leaving the mono HFP
   *  link. Returns what it paused, for resumeAudioElements. */
  parkAudioElements(): HTMLAudioElement[] {
    const parked: HTMLAudioElement[] = [];
    document
      .querySelectorAll<HTMLAudioElement>("audio[data-daw-mix], audio[data-daw-conv]")
      .forEach((el) => {
        if (!el.paused) {
          el.pause();
          parked.push(el);
        }
      });
    return parked;
  }

  resumeAudioElements(els: HTMLAudioElement[]): void {
    els.forEach((el) => void el.play().catch(() => {}));
  }

  suppressForLossless(on: boolean): void {
    // Switching the audible path invalidates the WebRTC jitter-buffer stats as
    // a latency refinement (the muted receiver's buffer says nothing about the
    // WS chain the listener actually hears). Drop the memory on every edge.
    if (on !== this.losslessActive) this.timecode.clearStatsRefinement();
    this.losslessActive = on;
    if (this.opusFadeRaf !== null) {
      cancelAnimationFrame(this.opusFadeRaf);
      this.opusFadeRaf = null;
    }
    if (this.audioEl) {
      this.audioEl.muted = on ? true : this.muted;
      this.audioEl.volume = on ? 0 : this.volume;
    }
  }

  /** True only in a secure context (https or localhost/127.0.0.1). Output-device
   *  APIs — selectAudioOutput() and setSinkId() — are unavailable on a plain
   *  http:// LAN origin, and enumerateDevices() returns unlabeled devices there. */
  static isSecureForAudioOutput(): boolean {
    return typeof window !== "undefined" && window.isSecureContext === true;
  }

  /**
   * Reveal real output-device LABELS: enumerateDevices() returns empty labels
   * until the page has been granted a device permission. We briefly open (and
   * immediately stop) a mic stream to unlock them, then re-enumerate. Call this
   * from a user gesture — it prompts for microphone permission. Returns the
   * (now hopefully labeled) output devices; on denial returns whatever we have.
   */
  async unlockOutputLabels(): Promise<MediaDeviceInfo[]> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
    } catch {
      /* permission denied — labels stay generic */
    }
    return this.listOutputDevices();
  }

  // ---- Level metering ------------------------------------------------------

  /** Tap the mix track with a read-only Web Audio analyser (per channel). Does
   *  NOT connect to the destination, so it never affects playback. Best-effort. */
  private setupMeter(track: RemoteAudioTrack): void {
    this.teardownMeter();
    try {
      const mst = track.mediaStreamTrack;
      if (!mst) return;
      const Ctx: typeof AudioContext | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(new MediaStream([mst]));
      const splitter = ctx.createChannelSplitter(2);
      src.connect(splitter);
      const aL = ctx.createAnalyser();
      const aR = ctx.createAnalyser();
      aL.fftSize = 1024;
      aR.fftSize = 1024;
      // Channel 1 may not exist for a mono source; splitter still delivers 0s.
      splitter.connect(aL, 0);
      splitter.connect(aR, 1);
      this.audioCtx = ctx;
      this.analyserL = aL;
      this.analyserR = aR;
      this.meterBuf = new Float32Array(aL.fftSize);
      // Resume if the gesture already happened (autoplay may leave it suspended).
      if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    } catch {
      this.teardownMeter();
    }
  }

  private teardownMeter(): void {
    this.analyserL = null;
    this.analyserR = null;
    if (this.audioCtx) {
      void this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
  }

  /** Current per-channel RMS + peak (linear 0..1), or null if unavailable.
   *  RMS drives a smooth integrated bar; peak drives a peak-hold marker. */
  getLevels():
    | { l: { rms: number; peak: number }; r: { rms: number; peak: number } }
    | null {
    if (!this.analyserL || !this.analyserR) return null;
    const measure = (a: AnalyserNode): { rms: number; peak: number } => {
      a.getFloatTimeDomainData(this.meterBuf);
      const n = this.meterBuf.length;
      let sumSq = 0;
      let peak = 0;
      for (let i = 0; i < n; i++) {
        const v = this.meterBuf[i] ?? 0;
        sumSq += v * v;
        const a2 = v < 0 ? -v : v;
        if (a2 > peak) peak = a2;
      }
      const rms = n > 0 ? Math.sqrt(sumSq / n) : 0;
      return { rms: rms > 1 ? 1 : rms, peak: peak > 1 ? 1 : peak };
    };
    return { l: measure(this.analyserL), r: measure(this.analyserR) };
  }

  /** True if this browser supports picking the audio output device (setSinkId). */
  static supportsOutputSelection(): boolean {
    return (
      typeof HTMLMediaElement !== "undefined" &&
      typeof (HTMLMediaElement.prototype as unknown as { setSinkId?: unknown }).setSinkId ===
        "function"
    );
  }

  /** List available audio output devices (best-effort; labels need permission). */
  async listOutputDevices(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      return all.filter((d) => d.kind === "audiooutput");
    } catch {
      return [];
    }
  }

  /** Route playback to a specific output device (Chrome/Edge; no-op if unsupported). */
  async setOutputDevice(deviceId: string): Promise<void> {
    this.sinkId = deviceId;
    const el = this.audioEl as (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (el && typeof el.setSinkId === "function") {
      try {
        await el.setSinkId(deviceId);
      } catch (e) {
        this.cb.onError?.(`Could not set output device: ${(e as Error).message}`);
      }
    }
    // §9 P6: conversation audio follows the same output device (best-effort) —
    // the WebAudio context for the voices, plus any element-fallback attaches.
    this.applyConvSink();
    for (const conv of this.convEls.values()) {
      if (conv.muted) continue; // WebAudio carries the sound; element is a pump
      const c = conv as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      if (typeof c.setSinkId === "function") await c.setSinkId(deviceId).catch(() => {});
    }
  }

  /** True if the browser offers the native output-device picker (Chrome 105+).
   *  This returns devices WITH names and needs no microphone permission. */
  static supportsOutputPicker(): boolean {
    return (
      typeof navigator !== "undefined" &&
      typeof (navigator.mediaDevices as unknown as { selectAudioOutput?: unknown })
        ?.selectAudioOutput === "function"
    );
  }

  /** Open the native output picker, route playback there, and return the chosen
   *  device's label (or null if cancelled / unsupported). */
  async pickOutputDevice(): Promise<string | null> {
    const md = navigator.mediaDevices as unknown as {
      selectAudioOutput?: () => Promise<MediaDeviceInfo>;
    };
    if (typeof md?.selectAudioOutput !== "function") return null;
    try {
      const info = await md.selectAudioOutput();
      if (info?.deviceId) {
        await this.setOutputDevice(info.deviceId);
        return info.label || "Output device";
      }
    } catch {
      /* user cancelled the picker */
    }
    return null;
  }

  /**
   * Set the WebRTC jitter-buffer target (ms) on the mix receiver — the actual
   * audio buffering knob behind the host-chosen listener latency. A larger
   * target trades responsiveness for robustness against network jitter; the
   * timecode readout is buffer-corrected by the same amount so display stays
   * synchronous with what is heard. Best-effort: not all browsers expose the
   * property. Remembered so it is re-applied on track re-subscribe.
   */
  setJitterBufferTarget(ms: number): void {
    this.jitterTargetMs = Math.max(0, Math.round(ms));
    this.applyJitterBufferTarget();
  }

  private applyJitterBufferTarget(): void {
    const receiver = this.getMixReceiver() as
      | (RTCRtpReceiver & { jitterBufferTarget?: number | null; playoutDelayHint?: number })
      | null;
    if (!receiver) return;
    try {
      // Modern, spec'd property (Chrome 116+, ms).
      if ("jitterBufferTarget" in receiver) {
        receiver.jitterBufferTarget = this.jitterTargetMs;
      }
      // Legacy Chrome hint (seconds).
      if ("playoutDelayHint" in receiver) {
        receiver.playoutDelayHint = this.jitterTargetMs / 1000;
      }
    } catch {
      /* property may be read-only on some builds; the display correction still applies */
    }
  }

  /** Tier-1 audio format for the info readout (docs §1.2 / §2). */
  formatInfo(): { codec: string; sampleRate: number; channels: number } {
    return { codec: "Opus", sampleRate: 48_000, channels: 2 };
  }

  /** Is the room able to play audio right now (no pending autoplay block)? */
  get canPlayback(): boolean {
    return this.room.canPlaybackAudio;
  }

  /** §14: the UI hands us the tile container; late so early subscribes wait in
   *  videoEls-less state (tracks arriving before this are attached on the next
   *  subscribe — in practice the container exists before connect()). */
  setVideoContainer(el: HTMLElement | null): void {
    this.videoContainer = el;
  }

  /** §14: camera/screen video of ACTIVE participants (anyone but the server
   *  publisher) renders for every listener. Muted — voices arrive separately
   *  as conversation audio; the mix path is untouched. */
  private attachVideo(track: RemoteTrack, sid: string): void {
    if (!this.videoContainer || this.videoEls.has(sid)) return;
    const el = track.attach() as HTMLVideoElement;
    el.muted = true;
    el.autoplay = true;
    el.setAttribute("playsinline", "");
    el.className = "videos__tile";
    this.videoContainer.appendChild(el);
    this.videoEls.set(sid, el);
    this.videoContainer.hidden = false;
  }

  private detachVideo(track: RemoteTrack): void {
    for (const [sid, el] of this.videoEls) {
      if (track.attachedElements.includes(el)) {
        track.detach(el);
        el.remove();
        this.videoEls.delete(sid);
      }
    }
    if (this.videoContainer && this.videoEls.size === 0) this.videoContainer.hidden = true;
  }

  private handleTrackSubscribed(
    track: RemoteTrack,
    pub: RemoteTrackPublication,
    participant: RemoteParticipant,
  ): void {
    if (track.kind === Track.Kind.Video) {
      if (!participant.identity.startsWith("ingest-")) this.attachVideo(track, pub.trackSid);
      return;
    }
    if (track.kind !== Track.Kind.Audio) return;
    if (pub.trackName !== MIX_TRACK_NAME) {
      // §9 P6 (additive): call/host microphones play alongside the mix so
      // listeners hear "Mix + Gespräch". Reached only when call participants
      // exist — with nobody publishing, behavior is byte-identical to before.
      if (isConversationAudio("audio", pub.trackName, participant.identity)) {
        this.attachConversation(
          track as RemoteAudioTrack,
          pub.trackSid,
          participant.identity,
          participant.name || participant.identity,
        );
      }
      return; // anything else (video, unknown publishers): ignore as before
    }

    if (!this.receiveMusic) { pub.setSubscribed(false); return; }
    this.mixTrack = track as RemoteAudioTrack;

    // Attach to a dedicated, hidden <audio> element appended to the DOM so the
    // browser actually routes it to the output device.
    const el = track.attach() as HTMLAudioElement;
    el.autoplay = true;
    el.setAttribute("data-daw-mix", "1");
    el.style.display = "none";
    // Apply current volume / mute / output-device to the fresh element. While
    // the WS player is the active output, hold the element silent — via MUTED
    // (iOS ignores volume, see suppressForLossless).
    el.volume = this.losslessActive ? 0 : this.volume;
    el.muted = this.losslessActive ? true : this.muted;
    document.body.appendChild(el);
    this.audioEl = el;
    if (this.sinkId) void this.setOutputDevice(this.sinkId);
    // Re-apply the host-chosen buffer to the fresh receiver.
    this.applyJitterBufferTarget();
    // Tap the mix for the level meter (read-only).
    this.setupMeter(this.mixTrack);

    this.cb.onMixState?.(true);
    // Try to start playback immediately; if blocked, the UI shows a tap-to-play.
    void this.ensureAudioPlayback();
  }

  /** §9 P6: attach one conversation mic to its own hidden element. Follows the
   *  user volume/mute/output-device but is NOT part of the lossless crossfade
   *  (lossless replaces only the mix). */
  private attachConversation(
    track: RemoteAudioTrack,
    sid: string,
    identity: string,
    label?: string,
  ): void {
    const el = track.attach() as HTMLAudioElement;
    el.autoplay = true;
    el.setAttribute("data-daw-conv", "1");
    el.style.display = "none";
    document.body.appendChild(el);
    this.convEls.set(sid, el);
    this.convTracks.set(sid, track);
    this.convIdentity.set(sid, identity);
    if (label) this.convLabels.set(identity, label);
    this.wireConvWebAudio(track, sid, el);
    this.cb.onConversations?.();
  }

  /** §16 mini-mixer: one entry per talking PARTICIPANT (deduped across their
   *  republished tracks), most-recent order of first appearance. */
  listConversations(): { id: string; label: string; muted: boolean }[] {
    const out: { id: string; label: string; muted: boolean }[] = [];
    // Everyone the host PROMOTED (canPublish) — not just whoever is currently
    // sending. A talker who mutes must stay in the list, otherwise "muted" and
    // "not in the call at all" looked identical (the row simply vanished).
    for (const p of this.room.remoteParticipants.values()) {
      if (p.identity.startsWith("ingest-")) continue; // the mix publisher
      // §23: the permission flag alone was the filter, and a participant whose
      // grant update never reached THIS client stayed invisible while being
      // perfectly audible — "den hab ich nicht gesehen, er konnte aber
      // trotzdem mit uns kommunizieren" (host, 22.08., about Lukas). Anyone
      // who publishes audio is in the call, whatever our copy of their
      // permissions says: if you can hear them, you can see them.
      const publishes = p.audioTrackPublications.size > 0;
      if (!p.permissions?.canPublish && !publishes) continue;
      const live = [...p.audioTrackPublications.values()].some(
        (pub) => pub.isSubscribed && !pub.isMuted && pub.track,
      );
      out.push({ id: p.identity, label: p.name || this.convLabels.get(p.identity) || "Guest", muted: !live });
    }
    return out;
  }

  /** §16: per-participant gain (unity = 1, ceiling +10 dB). Applied on top of
   *  volume/mute, to EVERY track this participant currently publishes. */
  setConversationGain(identity: string, gain: number): void {
    const g = Math.min(3.163, Math.max(0, gain)); // +10 dB ceiling
    this.convUserGain.set(identity, g);
    for (const [sid, ident] of this.convIdentity) {
      if (ident !== identity) continue;
      const n = this.convNodes.get(sid);
      if (n) n.gain.gain.value = this.muted ? 0 : this.convMaster * g;
    }
    this.ensureConversationAudible();
  }

  getConversationGain(identity: string): number {
    return this.convUserGain.get(identity) ?? 1;
  }

  /** Route one conversation track through the WebAudio graph (element stays a
   *  muted WebKit pump). Extracted so refreshConversationRouting can rebuild
   *  every track on a FRESH context. */
  private wireConvWebAudio(track: RemoteAudioTrack, sid: string, el: HTMLAudioElement): void {
    // iOS voice-route fix: keep the element (WebKit only pumps a remote track
    // that is attached to a media element) but MUTED — the audible path is the
    // WebAudio graph below, so the page never counts as a phone call (which
    // put a floor under the volume rocker and ducked the stream).
    try {
      const mst = track.mediaStreamTrack;
      if (!mst) throw new Error("no track");
      const Ctx: typeof AudioContext | undefined =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) throw new Error("no ctx");
      // Ride the stream player's running context when we have it.
      if (!this.sharedCtx) this.convCtx ??= new Ctx();
      const ctx = this.sharedCtx ?? this.convCtx!;
      const src = ctx.createMediaStreamSource(new MediaStream([mst]));
      // §26 low cut on every VOICE (never on the music): a second-order
      // high-pass, the console habit. It takes out desk thumps,
      // laptop fans, handling noise and room rumble, which are the parts of a
      // talkback mic that muddy a mix nobody is listening to yet. Costs one
      // biquad per voice — nothing worth measuring. Receive side on purpose:
      // filtering before the encoder would mean routing the CAPTURE through a
      // graph of our own, and that corner of iOS has cost this project enough.
      const cut = ctx.createBiquadFilter();
      cut.type = "highpass";
      cut.frequency.value = VOICE_LOW_CUT_HZ;
      cut.Q.value = 0.707; // Butterworth: -3 dB at the corner, no bump above it
      const gain = ctx.createGain();
      gain.gain.value = this.muted ? 0 : this.convMaster * this.gainForSid(sid);
      // Meter tap sits POST-gain (it shows what is heard) and passes through.
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(cut).connect(gain).connect(analyser).connect(ctx.destination);
      this.convNodes.set(sid, { src, cut, gain });
      this.convAnalysers.set(sid, analyser);
      el.muted = true;
      el.volume = 0;
      if (ctx.state !== "running") void ctx.resume().catch(() => {});
      if (this.sinkId) this.applyConvSink();
    } catch {
      // No WebAudio → the element carries the sound (pre-iOS-fix behavior).
      el.volume = this.convMaster;
      el.muted = this.muted;
      if (this.sinkId) {
        const sinkEl = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
        if (typeof sinkEl.setSinkId === "function") void sinkEl.setSinkId(this.sinkId).catch(() => {});
      }
    }
  }

  /** Adopt the stream player's AudioContext for the call voices (or drop back
   *  to our own when it goes away). Rewires the existing voices. */
  setSharedAudioContext(ctx: AudioContext | null): void {
    if (ctx === this.sharedCtx) return;
    this.sharedCtx = ctx;
    this.refreshConversationRouting();
  }

  /** Tear down and rebuild the conversation WebAudio graph on a FRESH context.
   *  iOS keeps a context created (or run) during a mic capture on the degraded
   *  voice output path even after the capture ends — only a new context gets
   *  the restored hi-fi route. */
  refreshConversationRouting(): void {
    for (const n of this.convNodes.values()) {
      try { n.src.disconnect(); n.cut.disconnect(); n.gain.disconnect(); } catch { /* torn down */ }
    }
    this.convNodes.clear();
    this.convAnalysers.clear();
    // Close only a context we own; the shared one belongs to the player.
    if (this.convCtx) {
      void this.convCtx.close().catch(() => {});
      this.convCtx = null;
    }
    for (const [sid, track] of this.convTracks) {
      const el = this.convEls.get(sid);
      if (el) this.wireConvWebAudio(track, sid, el);
    }
    // The fresh context is born SUSPENDED when no gesture is in flight (the
    // rebuild runs right after a mic release, not on a tap). Resume it, and
    // hand the voices to the elements until it actually renders — otherwise
    // this device goes deaf to the call while everyone still hears it.
    // ensureConversationAudible() resumes and installs the element fallback
    // (kept in that method: touching this.convCtx here confuses TS's narrowing
    // after the `= null` above, and one owner of the rule is cleaner anyway).
    this.ensureConversationAudible();
  }

  /** iOS audio-session flips (mic open/close) interrupt every AudioContext on
   *  the page without any event we could await — the UI watchdog calls this
   *  every second to claw them back. */
  resumeAudioContexts(): void {
    if (this.convCtx && this.convCtx.state !== "running")
      void this.convCtx.resume().catch(() => {});
    if (this.audioCtx && this.audioCtx.state !== "running")
      void this.audioCtx.resume().catch(() => {});
    this.ensureConversationAudible();
  }

  /** Voices must ALWAYS be audible. The WebAudio path is preferred (it keeps
   *  iOS out of its phone-call audio session), but a context created without a
   *  user gesture — e.g. the one rebuilt right after a mic release — can sit
   *  suspended forever, and then the call was silent on that device while the
   *  others still heard it (one-way audio). Whenever the context is not
   *  rendering, the (muted) <audio> elements take over; they go silent again
   *  the moment WebAudio runs, so the two paths are never audible together. */
  private ensureConversationAudible(): void {
    const active = this.sharedCtx ?? this.convCtx;
    if (active && active.state !== "running") void active.resume().catch(() => {});
    const webAudioLive = active !== null && active.state === "running";
    // Only hand over to the elements after the graph has been dead for a
    // while: flipping on every hiccup produced audible bursts when a
    // long-muted element was un-muted mid-stream.
    if (!webAudioLive) {
      if (this.convDeadSince === 0) this.convDeadSince = Date.now();
      if (Date.now() - this.convDeadSince < 2000) return;
    } else {
      this.convDeadSince = 0;
    }
    for (const [sid, el] of this.convEls) {
      const gain = this.gainForSid(sid);
      const wantMuted = webAudioLive || this.muted || gain === 0;
      const wantVolume = webAudioLive ? 0 : Math.min(1, this.convMaster * gain);
      if (el.muted !== wantMuted) el.muted = wantMuted;
      if (el.volume !== wantVolume) el.volume = wantVolume;
      if (!webAudioLive && el.paused) void el.play().catch(() => {});
    }
  }

  /** RMS (0..1) of an analyser's current block. */
  private rmsOf(an: AnalyserNode): number {
    const n = an.fftSize;
    if (this.levelBuf.length !== n) this.levelBuf = new Float32Array(n);
    an.getFloatTimeDomainData(this.levelBuf);
    let sum = 0;
    for (let i = 0; i < n; i++) {
      const v = this.levelBuf[i]!;
      sum += v * v;
    }
    return Math.sqrt(sum / n);
  }

  /** §17: current level (RMS 0..1) of a talking participant, across every
   *  track they publish. 0 when they are muted or not connected. */
  conversationLevel(identity: string): number {
    let peak = 0;
    for (const [sid, ident] of this.convIdentity) {
      if (ident !== identity) continue;
      const an = this.convAnalysers.get(sid);
      if (an) peak = Math.max(peak, this.rmsOf(an));
    }
    return peak;
  }

  /** §17: own microphone level (RMS 0..1). Lazily (re)wires to the currently
   *  published mic track — it is re-created on every auto-mute cycle — and
   *  returns 0 while nothing is published. */
  micLevel(): number {
    const pub = [...this.room.localParticipant.audioTrackPublications.values()][0];
    const mst = pub?.track?.mediaStreamTrack ?? null;
    if (!mst || mst.readyState !== "live") {
      if (this.micWiredTrack) this.stopMicMeter();
      return 0;
    }
    if (mst !== this.micWiredTrack) {
      this.stopMicMeter();
      try {
        const Ctx: typeof AudioContext | undefined =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return 0;
        this.micCtx ??= new Ctx();
        this.micSrc = this.micCtx.createMediaStreamSource(new MediaStream([mst]));
        this.micAnalyser = this.micCtx.createAnalyser();
        this.micAnalyser.fftSize = 1024;
        // Analyser only — NEVER connected to the destination (that would be a
        // feedback loop of your own voice through the speakers).
        this.micSrc.connect(this.micAnalyser);
        this.micWiredTrack = mst;
        if (this.micCtx.state !== "running") void this.micCtx.resume().catch(() => {});
      } catch {
        return 0;
      }
    }
    return this.micAnalyser ? this.rmsOf(this.micAnalyser) : 0;
  }

  /** Drop the own-mic meter graph (mic released / demoted / teardown). */
  stopMicMeter(): void {
    try { this.micSrc?.disconnect(); } catch { /* torn down */ }
    try { this.micAnalyser?.disconnect(); } catch { /* torn down */ }
    this.micSrc = null;
    this.micAnalyser = null;
    this.micWiredTrack = null;
  }

  /** Tear down one conversation track (element + WebAudio nodes + bookkeeping). */
  private dropConversation(sid: string, track?: RemoteTrack): void {
    const el = this.convEls.get(sid);
    if (el) {
      try { (track ?? this.convTracks.get(sid))?.detach(el); } catch { /* already detached */ }
      el.remove();
    }
    const n = this.convNodes.get(sid);
    if (n) {
      try { n.src.disconnect(); n.gain.disconnect(); } catch { /* torn down */ }
    }
    try { this.convAnalysers.get(sid)?.disconnect(); } catch { /* torn down */ }
    const identity = this.convIdentity.get(sid);
    this.convEls.delete(sid);
    this.convNodes.delete(sid);
    this.convAnalysers.delete(sid);
    this.convTracks.delete(sid);
    this.convIdentity.delete(sid);
    // Keep the label only while the participant still has a track.
    if (identity && ![...this.convIdentity.values()].includes(identity)) {
      this.convLabels.delete(identity);
    }
    this.cb.onConversations?.();
  }

  /** Reverse lookup for teardown paths without a publication. */
  private sidOfTrack(track: RemoteTrack): string | null {
    for (const [sid, t] of this.convTracks) if (t === track) return sid;
    return null;
  }

  /** A participant left the room: drop every conversation track of theirs. */
  private dropConversationsOf(identity: string): void {
    for (const [sid, ident] of [...this.convIdentity]) {
      if (ident === identity) this.dropConversation(sid);
    }
  }

  /** User gain of the participant owning this track sid (unity when unset). */
  private gainForSid(sid: string): number {
    const identity = this.convIdentity.get(sid);
    return identity ? (this.convUserGain.get(identity) ?? 1) : 1;
  }

  /** Best-effort output routing for the conversation WebAudio context
   *  (AudioContext.setSinkId is Chromium-only; irrelevant on iOS). */
  private applyConvSink(): void {
    const ctx = this.convCtx as (AudioContext & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (ctx && typeof ctx.setSinkId === "function" && this.sinkId)
      void ctx.setSinkId(this.sinkId).catch(() => {});
  }

  private handleTrackUnsubscribed(track: RemoteTrack, sid?: string): void {
    if (track.kind === Track.Kind.Video) {
      this.detachVideo(track);
      return;
    }
    if (track.kind !== Track.Kind.Audio) return;
    if (track !== this.mixTrack) {
      // §9 P6: a conversation mic went away — drop it by its TRACK SID (the
      // publication's own key). Matching on attachedElements missed tracks
      // LiveKit had already detached, which left ghost rows in the mixer.
      const key = sid && this.convEls.has(sid) ? sid : this.sidOfTrack(track);
      if (key) this.dropConversation(key, track);
      return;
    }
    track.detach().forEach((el) => el.remove());
    this.teardownMeter();
    this.mixTrack = null;
    this.audioEl = null;
    this.cb.onMixState?.(false);
  }

  private handleData(payload: Uint8Array, topic?: string, senderIdentity?: string): void {
    // tc/ctrl steer every listener's clock, comments and grants — accept them
    // ONLY from the server's ingest publisher. Any call participant could
    // otherwise forge them (defense in depth: participant tokens no longer
    // carry canPublishData either).
    if (senderIdentity !== undefined && !senderIdentity.startsWith("ingest-")) return;
    let obj: unknown;
    try {
      obj = JSON.parse(decoder.decode(payload));
    } catch {
      return; // ignore malformed packets
    }

    if (topic === TOPIC_TC) {
      const pkt = obj as Partial<TimecodePacket>;
      if (
        pkt &&
        pkt.t === "tc" &&
        typeof pkt.streamSamples === "number" &&
        typeof pkt.projectSec === "number"
      ) {
        this.timecode.ingest({
          t: "tc",
          seq: typeof pkt.seq === "number" ? pkt.seq : 0,
          streamSamples: pkt.streamSamples,
          projectSec: pkt.projectSec,
          playing: Boolean(pkt.playing),
          // §7.7: carry the display context through so the client can format the
          // *buffer-corrected* (heard) position — critical now that the buffer
          // can be up to 2 s, where a verbatim send-time label would be visibly
          // ahead of the audio.
          ...(pkt.disp && typeof pkt.disp === "object" ? { disp: pkt.disp } : {}),
          // Fallback REAPER-exact label, shown verbatim only when no disp.
          ...(typeof pkt.label === "string" ? { label: pkt.label } : {}),
          // Host-chosen listener latency/buffer (docs §7.7).
          ...(typeof pkt.latencyMs === "number" ? { latencyMs: pkt.latencyMs } : {}),
        });
      }
      return;
    }

    if (topic === TOPIC_CTRL) {
      const pkt = obj as CtrlPacket;
      if (!pkt || typeof pkt.t !== "string") return;
      switch (pkt.t) {
        case "comment":
          this.cb.onComment?.({
            id: pkt.id,
            projectSec: pkt.projectSec,
            text: pkt.text,
            author: pkt.author,
            ts: pkt.ts,
            ...(typeof pkt.clientId === "string" ? { clientId: pkt.clientId } : {}),
            ...(typeof pkt.color === "string" ? { color: pkt.color } : {}),
            ...(typeof pkt.edited === "boolean" ? { edited: pkt.edited } : {}),
          });
          return;
        case "comment_resolved":
          if (typeof pkt.id === "string" && typeof pkt.resolved === "boolean" && Number.isSafeInteger(pkt.resolutionVersion)) {
            this.cb.onCommentResolved?.(pkt.id, pkt.resolved, pkt.resolutionVersion);
          }
          break;
        case "comment_edit":
          if (typeof pkt.id === "string" && typeof pkt.text === "string") {
            this.cb.onCommentEdit?.(pkt.id, pkt.text);
          }
          return;
        case "comment_delete":
          if (typeof pkt.id === "string") this.cb.onCommentDelete?.(pkt.id);
          return;
        case "comment_config":
          this.cb.onCommentConfig?.({
            enabled: Boolean(pkt.enabled),
            privateMode: Boolean(pkt.privateMode),
          });
          return;
        case "listeners":
          if (typeof pkt.count === "number") this.cb.onListeners?.(pkt.count);
          return;
        case "stream_state":
          this.cb.onStreamState?.(Boolean(pkt.paused));
          return;
        case "listener_color":
          if (typeof pkt.clientId === "string" && typeof pkt.color === "string") {
            this.cb.onListenerColor?.(pkt.clientId, pkt.color);
          }
          return;
        case "perm":
          if (typeof pkt.canPause === "boolean" && typeof pkt.canCall === "boolean") {
            this.cb.onPerm?.(pkt.canPause, pkt.canCall);
          }
          return;
      }
      return;
    }
  }

  /**
   * Poll WebRTC stats and refine the playout delay from the audio receiver's
   * jitterBufferDelay (docs §4 step 3). Best-effort: not all browsers expose a
   * receiver here, in which case the configured/default delay stands.
   */
  private startStatsLoop(): void {
    if (this.statsTimer !== null) return;
    this.statsTimer = window.setInterval(() => {
      void this.sampleStats();
    }, 2000);
  }

  /** §23: how far behind each VOICE is, in ms, from its own receiver stats.
   *  A conversation that "felt like several seconds" (host, 22.08., with Alex)
   *  has to be measurable before it can be fixed — WebRTC jitter buffers grow
   *  under packet loss and do not always shrink back. Empty = not measured. */
  private convDelayMs = new Map<string, number>();
  /** Per-identity cumulative stats, for the delta between two samples. */
  private convDelayPrev = new Map<string, { delay: number; count: number }>();

  conversationDelayMs(identity: string): number {
    return this.convDelayMs.get(identity) ?? 0;
  }

  private async sampleConversationDelays(): Promise<void> {
    for (const [sid, identity] of this.convIdentity) {
      const track = this.convTracks.get(sid);
      const receiver = (track as unknown as { receiver?: RTCRtpReceiver } | undefined)?.receiver;
      if (!receiver) continue;
      let report: RTCStatsReport;
      try {
        report = await receiver.getStats();
      } catch {
        continue;
      }
      report.forEach((raw) => {
        const stat = raw as RTCStats & {
          kind?: string;
          mediaType?: string;
          jitterBufferDelay?: number;
          jitterBufferEmittedCount?: number;
        };
        if (
          (stat.kind !== "audio" && stat.mediaType !== "audio") ||
          typeof stat.jitterBufferDelay !== "number" ||
          typeof stat.jitterBufferEmittedCount !== "number"
        )
          return;
        const prev = this.convDelayPrev.get(identity);
        this.convDelayPrev.set(identity, {
          delay: stat.jitterBufferDelay,
          count: stat.jitterBufferEmittedCount,
        });
        if (!prev) return;
        // Delta, not lifetime average: a buffer that ballooned once and
        // recovered must not haunt the readout for the rest of the session.
        const dDelay = stat.jitterBufferDelay - prev.delay;
        const dCount = stat.jitterBufferEmittedCount - prev.count;
        if (dCount > 0) this.convDelayMs.set(identity, (dDelay / dCount) * 1000);
      });
    }
  }

  private async sampleStats(): Promise<void> {
    void this.sampleConversationDelays();
    const receiver = this.getMixReceiver();
    if (!receiver) return;
    let report: RTCStatsReport;
    try {
      report = await receiver.getStats();
    } catch {
      return;
    }
    report.forEach((s) => {
      // inbound-rtp (audio) carries jitterBufferDelay/EmittedCount in modern
      // browsers; some expose it on the media-source-less track stats instead.
      const stat = s as RTCStats & {
        kind?: string;
        mediaType?: string;
        jitterBufferDelay?: number;
        jitterBufferEmittedCount?: number;
      };
      if (
        (stat.kind === "audio" || stat.mediaType === "audio") &&
        typeof stat.jitterBufferDelay === "number" &&
        typeof stat.jitterBufferEmittedCount === "number"
      ) {
        // Refine the latency estimate ONLY while WebRTC is the audible path.
        if (!this.losslessActive)
          this.timecode.refineFromStats(stat.jitterBufferDelay, stat.jitterBufferEmittedCount);
        this.updateHealth(stat as RTCStats & {
          concealedSamples?: number;
          silentConcealedSamples?: number;
          jitterBufferEmittedCount?: number;
        });
      }
    });
  }

  /**
   * Derive a connection-health level (0 good / 1 marginal / 2 poor) from the
   * AUDIBLE jitter-buffer concealment rate — i.e. how often the buffer ran dry
   * and the decoder had to invent audio. A rising rate means the chosen latency
   * is too low for the network jitter, so the host should raise it.
   */
  private updateHealth(stat: {
    concealedSamples?: number;
    silentConcealedSamples?: number;
    jitterBufferEmittedCount?: number;
  }): void {
    const concealed = (stat.concealedSamples ?? 0) - (stat.silentConcealedSamples ?? 0);
    const emitted = stat.jitterBufferEmittedCount ?? 0;
    if (this.prevConcealed !== null && this.prevEmitted !== null && emitted > this.prevEmitted) {
      const dC = Math.max(0, concealed - this.prevConcealed);
      const dE = emitted - this.prevEmitted;
      const ratio = dC / dE; // fraction of this window's audio that was concealed
      const level = ratio > 0.02 ? 2 : ratio > 0.002 ? 1 : 0;
      this.healthWindows.push(level);
      if (this.healthWindows.length > 4) this.healthWindows.shift(); // ~8 s memory
    }
    this.prevConcealed = concealed;
    this.prevEmitted = emitted;
  }

  /** Worst connection-health level over the recent window (0 good … 2 poor). */
  getConnectionHealth(): number {
    return this.healthWindows.length ? Math.max(...this.healthWindows) : 0;
  }

  /** Reach into the underlying RTCRtpReceiver for the mix track, if available. */
  private getMixReceiver(): RTCRtpReceiver | null {
    if (!this.mixTrack) return null;
    // livekit-client exposes the receiver on the track at runtime; it is not in
    // the public typings, so we access it defensively.
    const r = (this.mixTrack as unknown as { receiver?: RTCRtpReceiver }).receiver;
    return r ?? null;
  }

  get isConnected(): boolean {
    return this.connected;
  }

  /** Disconnect and clean up timers + DOM elements. */
  async disconnect(): Promise<void> {
    this.intentional = true; // suppress the terminal-disconnect callback for our own teardown
    if (this.statsTimer !== null) {
      window.clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
    if (this.audioEl) {
      this.audioEl.remove();
      this.audioEl = null;
    }
    for (const el of this.convEls.values()) el.remove();
    this.convEls.clear();
    this.convTracks.clear();
    this.convIdentity.clear();
    this.convLabels.clear();
    this.convAnalysers.clear();
    this.stopMicMeter();
    if (this.micCtx) {
      void this.micCtx.close().catch(() => {});
      this.micCtx = null;
    }
    // Conversation WebAudio graph: nodes disconnected + context CLOSED — a
    // leaked AudioContext per re-join eventually exhausts iOS's hardware
    // context budget and talkback goes silent.
    for (const n of this.convNodes.values()) {
      try { n.src.disconnect(); n.gain.disconnect(); } catch { /* torn down */ }
    }
    this.convNodes.clear();
    if (this.convCtx) {
      void this.convCtx.close().catch(() => {});
      this.convCtx = null;
    }
    for (const el of this.videoEls.values()) el.remove();
    this.videoEls.clear();
    this.teardownMeter();
    this.mixTrack = null;
    await this.room.disconnect();
  }
}
